import React from "react";
import { FooterStoreLinks } from "./StoreLinks";
import { isNativePlatform } from "../utils/platform";

// Public marketing/content paths. Kept here so the crawlable links below render a real href
// that matches App.js routing (App's getUnauthedViewFromLocation / getUnauthedPath).
const VIEW_PATHS = {
  landing: "/",
  howItWorks: "/how-it-works",
  prePurchaseInspection: "/pre-purchase-inspection",
  autoRepair: "/auto-repair",
  vehicleInspection: "/vehicle-inspection",
  faq: "/faq",
  contact: "/contact",
  privacy: "/privacy",
  deleteAccount: "/delete-account",
  mobileApp: "/mobile-app",
  demo: "/demo",
  signup: "/signup",
  login: "/login",
};

// Crawlable SPA link: a real <a href> so search engines follow it, intercepted on click so users
// get instant in-app navigation instead of a full reload.
export function SpaLink({ view, onNavigate, className = "", children }) {
  const href = VIEW_PATHS[view] || "/";
  return (
    <a
      href={href}
      onClick={(event) => {
        event.preventDefault();
        onNavigate(view);
      }}
      className={className}
    >
      {children}
    </a>
  );
}

// Shared header + footer chrome for the public content pages, plus per-page JSON-LD injection.
// Googlebot renders JS and reads JSON-LD from the rendered DOM, so inline <script> tags are fine.
export default function MarketingLayout({ onNavigate, jsonLd = [], children }) {
  // The same bundle runs inside the installed Android app (and a Capgo OTA delivers web changes
  // straight into it), so a "download our app" link has to be hidden there or it ships into the
  // very app it is advertising.
  const showAppLinks = !isNativePlatform();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 text-slate-900 dark:text-gray-100">
      {jsonLd.map((schema, index) => (
        <script
          // eslint-disable-next-line react/no-danger
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      <header className="sticky top-0 z-20 border-b border-slate-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-4 py-3">
          {/* Same flex overflow as the landing header: without min-w-0 this link will not
              shrink below the wordmark and the text paints over the buttons at 360px. */}
          <SpaLink view="landing" onNavigate={onNavigate} className="flex min-w-0 items-center gap-2">
            <img src="/logo-192.png" alt="GearCheckAuto" width={32} height={32} className="shrink-0 rounded-lg" />
            <span className="truncate text-base font-bold tracking-tight text-slate-900 dark:text-gray-100 md:text-lg">GearCheckAuto</span>
          </SpaLink>
          <nav className="hidden items-center gap-6 md:flex">
            <SpaLink view="howItWorks" onNavigate={onNavigate} className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">
              How it works
            </SpaLink>
            <SpaLink view="prePurchaseInspection" onNavigate={onNavigate} className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">
              Pre-purchase inspections
            </SpaLink>
            <SpaLink view="autoRepair" onNavigate={onNavigate} className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">
              Auto repair
            </SpaLink>
            <SpaLink view="vehicleInspection" onNavigate={onNavigate} className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">
              Vehicle inspections
            </SpaLink>
            <SpaLink view="faq" onNavigate={onNavigate} className="text-sm font-medium text-slate-600 dark:text-gray-300 hover:text-blue-700">
              FAQ
            </SpaLink>
          </nav>
          {/* There is no mobile menu on these pages to hide Login behind, so both actions stay
              and the mobile padding tightens instead: 148 + 56 + 8 + 96 = 308 of the 328 available. */}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => onNavigate("login")}
              className="whitespace-nowrap rounded-lg px-2 py-2 text-sm font-semibold text-slate-700 dark:text-gray-200 hover:text-blue-700 md:px-3"
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="whitespace-nowrap rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500 md:px-4"
            >
              Get started
            </button>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-12 sm:grid-cols-2 md:grid-cols-4">
          <div className="sm:col-span-2 md:col-span-1">
            <div className="flex items-center gap-2">
              <img src="/logo-192.png" alt="GearCheckAuto" width={28} height={28} className="rounded-lg" />
              <span className="font-bold text-slate-900 dark:text-gray-100">GearCheckAuto</span>
            </div>
            <p className="mt-3 max-w-xs text-sm text-slate-600 dark:text-gray-300">
              Find the right auto repair shop or mobile mechanic near you. Send one request to several
              local shops, compare quotes and response times, and book the best fit.
            </p>
            {showAppLinks && <FooterStoreLinks />}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-gray-100">Guides</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-gray-300">
              <li><SpaLink view="howItWorks" onNavigate={onNavigate} className="hover:text-blue-700">How it works</SpaLink></li>
              <li><SpaLink view="prePurchaseInspection" onNavigate={onNavigate} className="hover:text-blue-700">Pre-purchase inspections</SpaLink></li>
              <li><SpaLink view="autoRepair" onNavigate={onNavigate} className="hover:text-blue-700">Auto repair help</SpaLink></li>
              <li><SpaLink view="vehicleInspection" onNavigate={onNavigate} className="hover:text-blue-700">Vehicle inspections</SpaLink></li>
              <li><SpaLink view="faq" onNavigate={onNavigate} className="hover:text-blue-700">FAQ</SpaLink></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-gray-100">Get started</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-gray-300">
              <li><SpaLink view="signup" onNavigate={onNavigate} className="hover:text-blue-700">Sign up</SpaLink></li>
              <li><SpaLink view="login" onNavigate={onNavigate} className="hover:text-blue-700">Log in</SpaLink></li>
              <li><SpaLink view="contact" onNavigate={onNavigate} className="hover:text-blue-700">Contact</SpaLink></li>
              {showAppLinks && (
                <li><SpaLink view="mobileApp" onNavigate={onNavigate} className="hover:text-blue-700">Get the app</SpaLink></li>
              )}
              <li><SpaLink view="demo" onNavigate={onNavigate} className="hover:text-blue-700">Try the demo</SpaLink></li>
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-gray-100">Home</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-gray-300">
              <li><SpaLink view="landing" onNavigate={onNavigate} className="hover:text-blue-700">GearCheckAuto home</SpaLink></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 dark:border-gray-700 py-4 text-center text-xs text-slate-500 dark:text-gray-400">
          <span>{new Date().getFullYear()} GearCheckAuto. All rights reserved.</span>{" "}
          <SpaLink view="privacy" onNavigate={onNavigate} className="underline hover:text-blue-700">
            Privacy Policy
          </SpaLink>{" "}
          <SpaLink view="deleteAccount" onNavigate={onNavigate} className="underline hover:text-blue-700">
            Delete account
          </SpaLink>
        </div>
      </footer>
    </div>
  );
}
